import React, { useCallback, useRef, useState } from "react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { WrapperComponent } from "../_shared-components/arrangement/WrapperComponent";
import { css } from "@emotion/react";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";

//@ts-expect-error
import { Textfit } from "react-textfit";
import cn from "classnames";
import { CSS_MQ } from "../../styles/breakpoints";
import type { ThreeDModelComponentType } from "@swo/storefront";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { useClickOutside } from "../../hooks/use-click-outside";
import { ModelInteractionHint } from "./model-interaction-hint";
import { usePrevious } from "../../hooks";
import { useStore } from "../../store/hooks/use-store";
import { useGlobalKeyListener } from "../../hooks/use-global-key-listener";
import { ThreeDModelState } from "./shared";

import { ModelCloseButton } from "./model-close";
import { CanvasClientOnly } from "./canvas-client-only";
import { useVisibilitySensor } from "../../hooks/use-visibility-sensor";
import { useTouchDeviceSensor } from "./use-touch-device-sensor";
import { useModelStateEffects } from "./use-model-state-effects";
import { ConfigurationOptions } from "./ConfigurationOptions";

export const ThreeDModelComponent: ReactTFC<ThreeDModelComponentType & ContentfulComponentCMSHook> = props => {
  const [modelState, setModelState] = useState<ThreeDModelState>("loading");
  const previousModelState = usePrevious(modelState);
  const { dispatch } = useStore();

  const [selectedOption, setSelectedOption] = useState(props.variants ? props.variants[0] : undefined);

  const canvasWrapperRef = useRef<any>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const { isVisible } = useVisibilitySensor(canvasWrapperRef, { partialVisibility: true, minTopValue: 50 });
  const isTouchDevice = useTouchDeviceSensor();

  const callback = useCallback(() => {
    setModelState(s => (s === "interacted-with" ? "idle" : s));
  }, [modelState]);

  useClickOutside(divRef, callback, true);
  useGlobalKeyListener("Escape", callback, [callback], true);

  useModelStateEffects(modelState, previousModelState, isTouchDevice, divRef, dispatch);

  const {
    headline,
    initialCameraPosition,
    polarRotation,
    model: { url: modelUrl },
  } = props;

  const themeClassName = `theme-${props.theme}`;
  const environmentUrl = props.environmentModel.url;

  return (
    <div
      ref={divRef}
      className={cn(themeClassName, modelState === "interacted-with" ? "interacted-with" : undefined)}
      css={containerStyles}
    >
      <WrapperComponent width={"content"} attachStyles={wrapperStyles}>
        {modelState !== "loading" && (
          <ModelInteractionHint
            modelState={modelState}
            onExitRequested={() => setModelState("idle")}
            buttonOnClick={() => setModelState("interacted-with")}
            translations={props.translations}
            link={props.link}
          />
        )}

        {headline && (
          <Textfit mode="single" min={20} max={500} forceWidth={false} css={headlineLayerStyles}>
            <EXPERIMENTAL_TextWithProductNameRenderer text={headline} />
          </Textfit>
        )}

        {modelState === "interacted-with" ? <ModelCloseButton onClick={() => setModelState("idle")} /> : null}

        {selectedOption && props.variants && (
          <ConfigurationOptions
            options={props.variants}
            selectedOption={selectedOption}
            onOptionSelected={setSelectedOption}
            translations={props.translations}
          />
        )}

        <div ref={canvasWrapperRef} css={canvasWrapperStyles}>
          <CanvasClientOnly
            initialCameraPosition={initialCameraPosition}
            polarRotation={polarRotation}
            modelState={modelState}
            setModelState={setModelState}
            modelUrl={modelUrl}
            environmentUrl={environmentUrl}
            isTouchDevice={isTouchDevice}
            backgroundTheme={props.theme}
            isVisible={!!isVisible}
            selectedOption={selectedOption}
          />
        </div>
      </WrapperComponent>
    </div>
  );
};

const wrapperStyles = css`
  display: flex;
  justify-content: center;

  position: relative;
  z-index: 2;

  --size: 400px;
  ${CSS_MQ.atLeastMd} {
    --size: 500px;
  }
  ${CSS_MQ.atLeastMd} {
    --size: 700px;
  }

  width: calc(2 * var(--size));
  height: var(--size);

  max-width: 100%;
  max-height: 100vh;

  ._button {
    background-color: var(--color);
  }
`;

const containerStyles = css`
  &.theme-white {
    --color: ${CSS_COLORS.White};
    --text-color: ${CSS_COLORS.Green};
  }

  &.theme-grey {
    --color: ${CSS_COLORS.Grey};
    --text-color: ${CSS_COLORS.Green};
  }

  &.theme-light-green {
    --color: ${CSS_COLORS.Green};
    --text-color: ${CSS_COLORS.White};
  }

  &.theme-green {
    --color: ${CSS_COLORS.GreenDark};
    --text-color: ${CSS_COLORS.White};
  }

  --text-opacity: 1;
  &.interacted-with {
    --text-opacity: 0.15;
  }

  background-color: var(--color);
  position: relative;
`;

const canvasWrapperStyles = css`
  z-index: 2;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const headlineLayerStyles = css`
  position: absolute;
  z-index: 1;
  width: calc(100% - 40px);

  --t: 20px;
  @media (pointer: coarse) {
    --t: 40px;
  }

  left: 20px;

  top: var(--t);
  height: calc(100% - var(--t));

  display: flex;
  justify-content: center;

  pointer-events: none;

  align-items: flex-start;

  ${CSS_MQ.atLeastMd} {
    align-items: center;
    width: 80%;
    left: 10%;
  }

  text-align: center;

  letter-spacing: -0.05em;

  color: var(--text-color);

  transition: opacity 0.3s linear;
  opacity: var(--text-opacity);

  > * {
    display: block;
    white-space: nowrap;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;
